const GALLERY_CACHE_PREFIX = "fpep_cms_gallery_cache_v2";
const DEFAULT_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

const parsePositiveIntegerEnv = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const cacheTtlMs = parsePositiveIntegerEnv(
  process.env.REACT_APP_CMS_FALLBACK_CACHE_TTL_MS,
  DEFAULT_CACHE_TTL_MS,
);

function canUseStorage() {
  try {
    return typeof window !== "undefined" && Boolean(window.localStorage);
  } catch (_error) {
    return false;
  }
}

function getCacheKey(key) {
  return `${GALLERY_CACHE_PREFIX}:${key}`;
}

function normalizeCacheLocale(locale = "me") {
  return String(locale || "me").toLowerCase().startsWith("en") ? "en" : "me";
}

function localizeCacheKey(key, locale = "me") {
  return `locale:${normalizeCacheLocale(locale)}:${key}`;
}

function readCache(key) {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(getCacheKey(key));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    const ttl = Number(parsed?.ttlMs || cacheTtlMs);

    if (!parsed || !createdAt || Date.now() - createdAt > ttl) {
      window.localStorage.removeItem(getCacheKey(key));
      return null;
    }

    return parsed.payload || null;
  } catch (_error) {
    return null;
  }
}

function writeCache(key, payload) {
  if (!canUseStorage() || payload === undefined || payload === null) return;

  try {
    window.localStorage.setItem(
      getCacheKey(key),
      JSON.stringify({
        createdAt: Date.now(),
        ttlMs: cacheTtlMs,
        payload,
      }),
    );
  } catch (_error) {
    // Private mode/quota errors must never break the public website.
  }
}

export async function fetchLocalGallery() {
  const response = await fetch("/data/galleryData.json", {
    headers: { Accept: "application/json" },
    cache: "no-cache",
  });

  if (!response.ok) {
    throw new Error("Local gallery JSON could not be loaded.");
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    throw new Error("galleryData.json nije niz (array).");
  }

  return data;
}

export function cacheCmsGalleryAlbums(albums = [], locale = "me") {
  writeCache(localizeCacheKey("albums", locale), albums);
}

export function getCachedCmsGalleryAlbums(locale = "me") {
  return readCache(localizeCacheKey("albums", locale)) || [];
}

export function cacheCmsGalleryAlbumDetail(slug, album, locale = "me") {
  if (!slug || !album) return;
  writeCache(localizeCacheKey(`album:${slug}`, locale), album);
}

export function getCachedCmsGalleryAlbumDetail(slug, locale = "me") {
  if (!slug) return null;
  return readCache(localizeCacheKey(`album:${slug}`, locale));
}

export function createGalleryFallbackNotice({ source = "local", reason = "unavailable" } = {}) {
  if (source === "cache") {
    return "CMS trenutno nije dostupan. Prikazana je posljednja sačuvana galerija.";
  }

  if (reason === "empty") {
    return "CMS trenutno nema objavljenih galerijskih albuma. Prikazana je postojeća lokalna galerija.";
  }

  return "CMS trenutno nije dostupan. Prikazana je postojeća lokalna galerija.";
}

export function getGalleryFallbackBadge(source = "local") {
  if (source === "cache") return "Cache";
  if (source === "local") return "Lokalna rezerva";
  return "CMS";
}

export function shouldSuppressGalleryFallbackNotice(error) {
  return error?.code === "CMS_DISABLED";
}
